// src/pages/admin/emails/AdminEmailTemplates.tsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabaseClient";
import TemplateForm from "./components/TemplateForm";

type TemplateRow = {
  id: string;
  name: string;
  subject: string;
  html_body: string;
  text_body: string;
  enabled: boolean;
  allow_tracking: boolean;
  dedupe_period: string;
  category: string;
  updated_at: string;
  created_at: string;
};

export default function AdminEmailTemplates() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<TemplateRow[]>([]);
  const [q, setQ] = useState("");
  const [onlyEnabled, setOnlyEnabled] = useState<boolean | "all">("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState<TemplateRow | null>(null);
  const [previewRow, setPreviewRow] = useState<TemplateRow | null>(null);

  const filtered = useMemo(() => {
    const f = rows.filter((r) => {
      const matchesQ =
        !q ||
        r.name.toLowerCase().includes(q.toLowerCase()) ||
        r.subject.toLowerCase().includes(q.toLowerCase()) ||
        r.category.toLowerCase().includes(q.toLowerCase());
      const matchesEnabled =
        onlyEnabled === "all" ? true : r.enabled === Boolean(onlyEnabled);
      return matchesQ && matchesEnabled;
    });
    return f.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  }, [rows, q, onlyEnabled]);

  async function fetchRows() {
    setLoading(true);
    const { data, error } = await supabase
      .from("email_templates")
      .select("*")
      .order("updated_at", { ascending: false });
    if (error) {
      console.error(error);
      setRows([]);
    } else {
      setRows((data ?? []) as TemplateRow[]);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchRows();
  }, []);

  function onCreate() {
    setEditRow(null);
    setFormOpen(true);
  }
  function onEdit(row: TemplateRow) {
    setEditRow(row);
    setFormOpen(true);
  }
  function onPreview(row: TemplateRow) {
    setPreviewRow(row);
  }

  async function toggleEnabled(row: TemplateRow) {
    const { error } = await supabase
      .from("email_templates")
      .update({ enabled: !row.enabled })
      .eq("id", row.id);
    if (error) return alert(error.message);
    fetchRows();
  }

  async function duplicateTemplate(row: TemplateRow) {
    const copyName = suggestCopyName(row.name);
    const payload = {
      name: copyName,
      subject: row.subject,
      html_body: row.html_body,
      text_body: row.text_body,
      enabled: false, // Sicherheitsstandard
      allow_tracking: row.allow_tracking,
      dedupe_period: row.dedupe_period,
      category: row.category,
    };
    const { error } = await supabase.from("email_templates").insert(payload);
    if (error) return alert(error.message);
    fetchRows();
  }

  async function deleteTemplate(row: TemplateRow) {
    const really = window.confirm( // Füge 'window.' hinzu
    `Vorlage "${row.name}" wirklich löschen?\nHinweis: Nur löschen, wenn sie nicht mehr in Regeln verwendet wird.`
    );
    if (!really) return;
    const { error } = await supabase.from("email_templates").delete().eq("id", row.id);
    if (error) return alert(error.message);
    fetchRows();
  }

  function onSaved() {
    setFormOpen(false);
    setEditRow(null);
    fetchRows();
  }

  return (
    <div className="max-w-6xl mx-auto mt-10 p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-sky-400">📑 E-Mail-Templates</h1>
        <div className="flex items-center gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suche (Name, Betreff, Kategorie)…"
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded-lg w-64 outline-none focus:border-sky-400 placeholder:text-slate-600"
          />
          <select
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded-lg outline-none focus:border-sky-400"
            value={String(onlyEnabled)}
            onChange={(e) =>
              setOnlyEnabled(
                e.target.value === "all" ? "all" : e.target.value === "true"
              )
            }
          >
            <option value="all">Alle</option>
            <option value="true">Nur aktiv</option>
            <option value="false">Nur inaktiv</option>
          </select>
          <button
            onClick={onCreate}
            className="px-4 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600 transition-colors"
          >
            + Neue Vorlage
          </button>
        </div>
      </div>

      <p className="text-slate-400 mb-4">
        Hier verwaltest du E-Mail-Vorlagen. Aktivierte Vorlagen können vom
        Sender verarbeitet werden (abhängig von Flags/Modus).
      </p>

      {loading ? (
        <div className="p-6 border border-[#2a3347] rounded-xl bg-[#161b27] text-slate-400">Lade…</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 border border-[#2a3347] rounded-xl bg-[#161b27] text-slate-500">
          Keine Vorlagen gefunden.
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#2a3347] rounded-xl bg-[#161b27]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#1e2639] text-left">
              <tr>
                <th className="px-4 py-3 font-semibold text-slate-300">Name</th>
                <th className="px-4 py-3 font-semibold text-slate-300">Betreff</th>
                <th className="px-4 py-3 font-semibold text-slate-300">Kategorie</th>
                <th className="px-4 py-3 font-semibold text-slate-300">Tracking</th>
                <th className="px-4 py-3 font-semibold text-slate-300">Dedupe</th>
                <th className="px-4 py-3 font-semibold text-slate-300">Aktiv</th>
                <th className="px-4 py-3 font-semibold text-slate-300">Aktualisiert</th>
                <th className="px-4 py-3 font-semibold text-slate-300 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-[#2a3347]">
                  <td className="px-4 py-3 text-slate-100">{r.name}</td>
                  <td className="px-4 py-3 text-slate-300">{r.subject}</td>
                  <td className="px-4 py-3 text-slate-300">{r.category}</td>
                  <td className="px-4 py-3 text-slate-300">
                    {r.allow_tracking ? "✅" : "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-300">{r.dedupe_period}</td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        "inline-flex px-2 py-0.5 rounded-full text-xs " +
                        (r.enabled
                          ? "bg-emerald-900/30 text-emerald-400"
                          : "bg-[#2a3347] text-slate-400")
                      }
                    >
                      {r.enabled ? "aktiv" : "inaktiv"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400">
                    {new Date(r.updated_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => onPreview(r)}
                        className="px-2 py-1 text-sky-400 hover:underline"
                      >
                        Vorschau
                      </button>
                      <button
                        onClick={() => onEdit(r)}
                        className="px-2 py-1 text-slate-300 hover:underline"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => toggleEnabled(r)}
                        className="px-2 py-1 text-slate-300 hover:underline"
                      >
                        {r.enabled ? "Deaktivieren" : "Aktivieren"}
                      </button>
                      <button
                        onClick={() => duplicateTemplate(r)}
                        className="px-2 py-1 text-slate-300 hover:underline"
                      >
                        Duplizieren
                      </button>
                      <button
                        onClick={() => deleteTemplate(r)}
                        className="px-2 py-1 text-red-400 hover:underline"
                      >
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Formular-Drawer (einfach als Modal gelöst) */}
      {formOpen && (
        <TemplateForm
          initial={editRow ?? undefined}
          onClose={() => {
            setFormOpen(false);
            setEditRow(null);
          }}
          onSaved={onSaved}
        />
      )}

      {/* Vorschau-Modal */}
      {previewRow && (
        <PreviewDialog row={previewRow} onClose={() => setPreviewRow(null)} />
      )}

      <div className="mt-8">
        <Link to="/admin/emails" className="text-sky-400 hover:underline">
          ← Zurück zum E-Mail-Bereich
        </Link>
      </div>
    </div>
  );
}

function suggestCopyName(name: string) {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  return `${name}-copy-${stamp}`;
}

function PreviewDialog({
  row,
  onClose,
}: {
  row: TemplateRow;
  onClose: () => void;
}) {
  // Dummy-Variablen für Vorschau – im echten Versand ersetzt der Sender die Platzhalter
  const vars = {
    first_name: "Alex",
    trip_name: "Testreise",
    cta_url: "https://svevana.app/trip/preview",
  };

  // ganz einfache Platzhalter-Auflösung (muss mit Sender konsistent sein)
  const html = row.html_body.replace(/\{\{\s*([a-zA-Z0-9_\.]+)\s*\}\}/g, (_m, key) => {
    const v = (vars as any)[key];
    return v == null ? "" : String(v);
  });

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-[#161b27] border border-[#2a3347] w-full max-w-3xl rounded-xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a3347]">
          <h3 className="font-semibold text-lg text-slate-100">Vorschau: {row.name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200">
            ✕
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          <div className="p-4 border-r border-[#2a3347]">
            <div className="text-xs uppercase text-slate-500 mb-2">HTML</div>
            <iframe
              title="html-preview"
              className="w-full h-[60vh] border border-[#2a3347] rounded"
              srcDoc={html}
            />
          </div>
          <div className="p-4">
            <div className="text-xs uppercase text-slate-500 mb-2">Text</div>
            <pre className="whitespace-pre-wrap text-sm p-3 bg-[#1e2639] rounded h-[60vh] overflow-auto text-slate-300">
{row.text_body
  .replace(/\{\{\s*first_name\s*\}\}/g, vars.first_name)
  .replace(/\{\{\s*trip_name\s*\}\}/g, vars.trip_name)
  .replace(/\{\{\s*cta_url\s*\}\}/g, vars.cta_url)}
            </pre>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-[#2a3347] flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
